import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Upload, Camera, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

function CheckinModal({ isOpen, onClose, onComplete }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    weight: '',
    body_fat: '',
    waist: '',
    notes: ''
  });
  const [files, setFiles] = useState({
    photo_front: null,
    photo_side: null,
    photo_back: null
  });

  const handleFileChange = (e, type) => {
    if (e.target.files[0]) {
      setFiles({ ...files, [type]: e.target.files[0] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();
      data.append('weight', formData.weight);
      if (formData.body_fat) data.append('body_fat', formData.body_fat);
      if (formData.waist) data.append('waist', formData.waist);
      if (formData.notes) data.append('notes', formData.notes);
      
      if (files.photo_front) data.append('photo_front', files.photo_front);
      if (files.photo_side) data.append('photo_side', files.photo_side);
      if (files.photo_back) data.append('photo_back', files.photo_back);

      const token = localStorage.getItem('token');
      await axios.post(`${API}/checkin`, data, {
        headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
        }
      });

      onComplete();
      onClose();
    } catch (error) {
      console.error("Error submitting checkin:", error);
      alert(error.response?.data?.detail || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gold text-white max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-gold flex items-center gap-2">
            <CheckCircle2 className="h-6 w-6" /> Check-in Semanal PRO
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Datos Numéricos */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Peso (kg) *</Label>
              <Input 
                type="number" step="0.1" required
                value={formData.weight}
                onChange={(e) => setFormData({...formData, weight: e.target.value})}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="75.5"
              />
            </div>
            <div className="space-y-2">
              <Label>% Grasa (opcional)</Label>
              <Input 
                type="number" step="0.1"
                value={formData.body_fat}
                onChange={(e) => setFormData({...formData, body_fat: e.target.value})}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="15.0"
              />
            </div>
            <div className="space-y-2">
              <Label>Cintura (cm) (opcional)</Label>
              <Input 
                type="number" step="0.1"
                value={formData.waist}
                onChange={(e) => setFormData({...formData, waist: e.target.value})}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="80"
              />
            </div>
          </div>

          {/* Fotos */}
          <div className="space-y-3 border-t border-gray-800 pt-4">
            <Label className="text-gray-400">Fotos de Progreso (Opcional)</Label>
            <div className="grid grid-cols-3 gap-2">
                {[
                    { key: 'photo_front', label: 'Frente' },
                    { key: 'photo_side', label: 'Perfil' },
                    { key: 'photo_back', label: 'Espalda' }
                ].map((item) => (
                    <div key={item.key} className="relative">
                        <input
                            type="file"
                            accept="image/*"
                            id={`file-${item.key}`}
                            className="hidden"
                            onChange={(e) => handleFileChange(e, item.key)}
                        />
                        <label 
                            htmlFor={`file-${item.key}`}
                            className={`flex flex-col items-center justify-center h-24 rounded border border-dashed cursor-pointer transition-colors
                                ${files[item.key] ? 'border-green-500 bg-green-900/20' : 'border-gray-600 hover:border-gold hover:bg-gray-800'}
                            `}
                        >
                            <Camera className={`h-6 w-6 mb-1 ${files[item.key] ? 'text-green-500' : 'text-gray-400'}`} />
                            <span className="text-[10px] text-gray-400">{item.label}</span>
                        </label>
                    </div>
                ))}
            </div>
          </div>

          {/* Notas */}
          <div className="space-y-2">
            <Label>Notas / Sensaciones</Label>
            <Textarea 
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="bg-gray-800 border-gray-700 text-white min-h-[80px]"
                placeholder="¿Cómo te has sentido esta semana?"
            />
          </div>

          <Button type="submit" className="w-full btn-primary-mk" disabled={loading}>
            {loading ? 'Guardando...' : 'Registrar Check-in'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CheckinModal;
